/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Form, Modal, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import {
    ECisOmStav,
    EOmezPrevodVyjadreni,
    IOmPrehledInput,
    IOmPrehledOut,
    IOmPrevod,
    omPrehledGet
} from 'services/dokumenty/omezPrevoditelnost';
import { dokOmezPrevodPrikazPut } from 'services/dokumenty/prikazy';
import { dokumentDownload, dokumentZalozeniApi } from 'services/dokumenty/smlouva';

import { EPodTypDokumentu, EStavZadostiDok, ETypDokumentu } from 'enums/dokumentyEnums';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

const OmezenaProveditelnost: React.FC = () => {
    const { confirm } = Modal;
    const { language, dokumenty, aktivniFirma } = useContext(Context);
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const [formFilter] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [filterValues, setFilterValues] = useState<IOmPrehledInput>({
        offset: 0,
        limit: 10,
        order: 'oid:desc',
        stav: ECisOmStav.KEKONTROLE
    });
    const [data, setData] = useState<IOmPrehledOut>();

    const renderConfirmModal = (row: IOmPrevod, stav: EOmezPrevodVyjadreni) =>
        confirm({
            title: <TranslateText>{stav === 'SOUHLAS' ? 'OM_ODSOUHLASIT_TEXT' : 'OM_ZAMITNOUT_TEXT'}</TranslateText>,
            cancelText: <TranslateText>NE</TranslateText>,
            okText: <TranslateText>ANO</TranslateText>,
            onOk() {
                setLoading(true);
                dokumentZalozeniApi({
                    typ: ETypDokumentu.PRIKAZY,
                    podtyp: EPodTypDokumentu.OMEZPREVOD
                })
                    .then(resp => {
                        dokOmezPrevodPrikazPut(resp.id, {
                            //...row,
                            idomprevod: row.id!,
                            vyjadreni: stav,
                            souhlas: false
                        })
                            .then(() => {
                                setLevel('dokumenty', {
                                    ...dokumenty,
                                    selectedZadost: {
                                        iddok: resp.id,
                                        typ: ETypDokumentu.PRIKAZY,
                                        podtyp: EPodTypDokumentu.OMEZPREVOD,
                                        stav: EStavZadostiDok.ROZP,
                                        isin: row.isin
                                    }
                                    // prehledData: prehled
                                });
                                navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.DOKUMENTY_NOVY}`);
                                /*  setFilterValues(pv => {
                                    return {
                                        ...pv
                                    };
                                }); */
                            })
                            .finally(() => setLoading(false));
                    })
                    .catch(() => setLoading(false));
            }
        });

    useEffect(() => {
        formFilter.setFieldValue('stav', 'KEKONTROLE');
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            filterValues &&
                (setLoading(true),
                omPrehledGet(
                    Object.fromEntries(
                        Object.entries({
                            ...filterValues
                        }).filter(([, v]) => v !== undefined && v !== '')
                    ) as unknown as IOmPrehledInput
                )
                    .then(resp => {
                        setData(resp);
                    })
                    .catch(() => setData(undefined))
                    .finally(() => setLoading(false)));
        }, 0);
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValues]);

    return (
        <LoadingWrapper loading={loading}>
            <Row>
                <Col span={24}>
                    <Title text="OM_TITLE" level={4} />

                    <FormWrapper
                        onFinish={(values: any) =>
                            setFilterValues(pv => {
                                return {
                                    ...pv,
                                    ...values,
                                    ...(values.datum && {
                                        datum: values.datum.format('YYYY-MM-DD')
                                    }),
                                    ...(values.dtexpirace && {
                                        dtexpirace: values.dtexpirace.format('YYYY-MM-DD')
                                    })
                                };
                            })
                        }
                        outForm={formFilter}
                    >
                        <Row gutter={16}>
                            <Col span={2}>
                                <InputComponent name="isin" label="ISIN" notRequired={true} />
                            </Col>
                            <Col span={2}>
                                <DatePickerComponent name="datum" label="DATUM" notRequired={true} />
                            </Col>
                            <Col span={2}>
                                <DatePickerComponent name="dtexpirace" label="DATUM_EXPIRACE" notRequired={true} />
                            </Col>
                            <Col span={2}>
                                <SelectComponent
                                    isFormItem={true}
                                    name="stav"
                                    labelParagraph="STAV_PREVODU"
                                    data={[
                                        {
                                            labelcz: 'OM_POZADAVEK_KEKONTROLE',
                                            value: 'KEKONTROLE'
                                        },
                                        {
                                            labelcz: 'OM_POZADAVEK_ODSOUHLAS',
                                            value: 'ODSOUHLAS'
                                        },
                                        {
                                            labelcz: 'OM_POZADAVEK_ZAMITNUT',
                                            value: 'ZAMITNUT'
                                        },
                                        {
                                            labelcz: 'OM_POZADAVEK_EXPIROVAL',
                                            value: 'EXPIROVAL'
                                        }
                                    ]}
                                    notRequired={true}
                                />
                            </Col>
                            <Col span={3}>
                                <SelectComponent
                                    isFormItem={true}
                                    name="dokstav"
                                    labelParagraph="STAV_PRIKAZU"
                                    data={[
                                        {
                                            labelcz: 'OM_STAV_ROZPRACOVANY',
                                            value: EStavZadostiDok.ROZP
                                        },
                                        {
                                            labelcz: 'OM_STAV_DOPL',
                                            value: EStavZadostiDok.DOPL
                                        },
                                        {
                                            labelcz: 'OM_STAV_KDOPLNENI',
                                            value: EStavZadostiDok.KDOPLNENI
                                        },
                                        {
                                            labelcz: 'OM_STAV_AUTKONTROLA',
                                            value: EStavZadostiDok.AUTKONTROLA
                                        },
                                        {
                                            labelcz: 'OM_STAV_AUTKONTROLAPRED',
                                            value: EStavZadostiDok.AUTKONTROLAPRED
                                        },
                                        {
                                            labelcz: 'OM_STAV_CDCPKEKONTR',
                                            value: EStavZadostiDok.CDCPKEKONTR
                                        },
                                        {
                                            labelcz: 'OM_STAV_CDCPKEKONTRPRED',
                                            value: EStavZadostiDok.CDCPKEKONTRPRED
                                        },
                                        {
                                            labelcz: 'OM_STAV_CDCPKONTR',
                                            value: EStavZadostiDok.CDCPKONTR
                                        },
                                        {
                                            labelcz: 'OM_STAV_CDCPKONTRPRED',
                                            value: EStavZadostiDok.CDCPKONTRPRED
                                        },
                                        {
                                            labelcz: 'OM_STAV_KPODPISU',
                                            value: EStavZadostiDok.KPODPISU
                                        },
                                        {
                                            labelcz: 'OM_STAV_PODEPSANO',
                                            value: EStavZadostiDok.PODEPSANO
                                        },
                                        {
                                            labelcz: 'OM_STAV_HOTOVOIIS',
                                            value: EStavZadostiDok.HOTOVOIIS
                                        },
                                        {
                                            labelcz: 'OM_STAV_ZAMITNUTO',
                                            value: EStavZadostiDok.ZAMITNUTO
                                        },
                                        {
                                            labelcz: 'OM_STAV_ZPRACOVANO',
                                            value: EStavZadostiDok.ZPRACOVANO
                                        },
                                        {
                                            labelcz: 'OM_STAV_ZKONTROLOVANO',
                                            value: EStavZadostiDok.ZKONTROLOVANO
                                        },
                                        {
                                            labelcz: 'OM_STAV_AKCEPTOVANO',
                                            value: EStavZadostiDok.AKCEPTOVANO
                                        },
                                        {
                                            labelcz: 'OM_STAV_ZRUSENO',
                                            value: EStavZadostiDok.ZRUSENO
                                        }
                                    ]}
                                    notRequired={true}
                                />
                            </Col>
                            <Col span={3}>
                                <InputComponent name="prevodceUcet" label="PRODVAJICI_UCET" notRequired={true} />
                            </Col>
                            <Col span={3}>
                                <InputComponent name="prevodce" label="PRODVAJICI_JMENO" notRequired={true} />
                            </Col>
                            <Col span={3}>
                                <InputComponent name="nabyvatelUcet" label="KUPUJICI_UCET" notRequired={true} />
                            </Col>
                            <Col span={2}>
                                <InputComponent name="nabyvatel" label="KUPUJICI_JMENO" notRequired={true} />
                            </Col>
                            <Col span={2}>
                                <InputComponent name="pocetcp" label="POCET_CP" notRequired={true} />
                            </Col>
                        </Row>

                        <Row
                            className="button-row"
                            style={{
                                top: -10,
                                margin: 0,
                                position: 'relative'
                            }}
                        >
                            <ButtonComponent buttontext={'FILTROVAT'} htmlType="submit" id="filtSubmitBtn" />
                            <ButtonComponent
                                buttontext={'VSECHNY'}
                                onClick={() => {
                                    formFilter.resetFields();
                                    setFilterValues({
                                        offset: 0,
                                        limit: 10,
                                        order: 'oid:desc'
                                    });
                                }}
                                htmlType="button"
                                id="filtClearBtn"
                            />
                        </Row>
                    </FormWrapper>
                    <Row gutter={8}>
                        <Col span={24}>
                            <Table
                                size="small"
                                bordered={true}
                                columns={[
                                    {
                                        title: <TranslateText>ISIN</TranslateText>,
                                        key: 'isin',
                                        dataIndex: 'isin'
                                    },
                                    {
                                        title: <TranslateText>DATUM</TranslateText>,
                                        key: 'dttransfer',
                                        dataIndex: 'dttransfer',
                                        render: (dttransfer: string) => {
                                            return dttransfer ? dayjs(dttransfer).format('DD.MM.YYYY') : undefined;
                                        }
                                    },
                                    {
                                        title: <TranslateText>DATUM_EXPIRACE</TranslateText>,
                                        key: 'dtexpirace',
                                        dataIndex: 'dtexpirace',
                                        render: (datum: string) => {
                                            return datum ? dayjs(datum).format('DD.MM.YYYY') : undefined;
                                        }
                                    },
                                    {
                                        title: <TranslateText>STAV_PREVODU</TranslateText>,
                                        key: 'stav',
                                        dataIndex: 'stav',
                                        render: stav => getLanguageObject('cz')[`OM_POZADAVEK_${stav}`]
                                    },
                                    {
                                        title: <TranslateText>STAV_PRIKAZU</TranslateText>,
                                        key: 'dokstav',
                                        dataIndex: 'dokstav',
                                        render: stav => getLanguageObject('cz')[`OM_STAV_${stav}`]
                                    },
                                    {
                                        title: (
                                            <>
                                                <Row>
                                                    <TranslateText>PRODAVAJICI</TranslateText>
                                                </Row>
                                                <Row>
                                                    <TranslateText>CISLO_MAJETKOVEHO_UCTU</TranslateText>
                                                </Row>
                                            </>
                                        ),
                                        key: 'prevodce',
                                        dataIndex: 'prevodce',
                                        render: val => val.alinearid
                                    },
                                    {
                                        title: (
                                            <>
                                                <Row>
                                                    <TranslateText>PRODAVAJICI</TranslateText>
                                                </Row>
                                                <Row>
                                                    <TranslateText>NAZEV_JMENO</TranslateText>
                                                </Row>
                                            </>
                                        ),
                                        key: 'prevodce',
                                        dataIndex: 'prevodce',
                                        render: val => `${val.ttma || ''} ${val.jmma || ''} ${val.pjma || ''}`
                                    },
                                    {
                                        title: (
                                            <>
                                                <Row>
                                                    <TranslateText>KUPUJICI</TranslateText>
                                                </Row>
                                                <Row>
                                                    <TranslateText>CISLO_MAJETKOVEHO_UCTU</TranslateText>
                                                </Row>
                                            </>
                                        ),
                                        key: 'nabyvatel',
                                        dataIndex: 'nabyvatel',
                                        render: val => val.alinearid
                                    },
                                    {
                                        title: (
                                            <>
                                                <Row>
                                                    <TranslateText>KUPUJICI</TranslateText>
                                                </Row>
                                                <Row>
                                                    <TranslateText>NAZEV_JMENO</TranslateText>
                                                </Row>
                                            </>
                                        ),
                                        key: 'nabyvatel',
                                        dataIndex: 'nabyvatel',
                                        render: val => `${val.ttma || ''} ${val.jmma || ''} ${val.pjma || ''}`
                                    },
                                    {
                                        title: <TranslateText>POCET_CP</TranslateText>,
                                        key: 'qtet',
                                        dataIndex: 'qtet'
                                    },

                                    {
                                        key: 'id',
                                        dataIndex: 'id',
                                        render: (_id, row, index) => (
                                            <Row>
                                                {row.stav === ECisOmStav.KEKONTROLE && !row.idprik && (
                                                    <>
                                                        <a
                                                            style={{
                                                                marginRight: 6
                                                            }}
                                                            onClick={() =>
                                                                renderConfirmModal(row, EOmezPrevodVyjadreni.SOUHLAS)
                                                            }
                                                            id={`souhlasBtn${index}`}
                                                        >
                                                            <TranslateText>OM_ODSOUHLASIT</TranslateText>
                                                        </a>
                                                        <a
                                                            onClick={() =>
                                                                renderConfirmModal(row, EOmezPrevodVyjadreni.NESOUHLAS)
                                                            }
                                                            id={`zamitnutBtn${index}`}
                                                        >
                                                            <TranslateText>OM_ZAMITNOUT</TranslateText>
                                                        </a>
                                                    </>
                                                )}
                                                {row.idprik && (
                                                    <a
                                                        onClick={() => {
                                                            if (
                                                                row.dokstav === EStavZadostiDok.AKCEPTOVANO ||
                                                                row.dokstav === EStavZadostiDok.ZRUSENO
                                                            ) {
                                                                const downloadFileName = `${aktivniFirma?.ic} - ${row?.isin ? row?.isin + ' - ' : ''}${getLanguageObject(language).DOKUMENTY_PRIKAZ_OMEZPREVOD}.pdf`;
                                                                setLoading(true);

                                                                dokumentDownload(row.idprik!, downloadFileName).finally(
                                                                    () => setLoading(false)
                                                                );
                                                            } else {
                                                                setLevel('dokumenty', {
                                                                    ...dokumenty,
                                                                    selectedZadost: {
                                                                        iddok: row.idprik!,
                                                                        typ: ETypDokumentu.PRIKAZY,
                                                                        podtyp: EPodTypDokumentu.OMEZPREVOD,
                                                                        stav: row.dokstav,
                                                                        isin: row.isin
                                                                    }
                                                                    // prehledData: prehled
                                                                });
                                                                navigateWorkaround(
                                                                    `/firma/${aktivniFirma?.iducet}${routePathEnum.DOKUMENTY_NOVY}`
                                                                );
                                                            }
                                                        }}
                                                        id={`prikazBtn${index}`}
                                                    >
                                                        <TranslateText>PRIKAZ</TranslateText>
                                                    </a>
                                                )}
                                            </Row>
                                        )
                                    }
                                ]}
                                rowKey={(_, i) => i || 0}
                                dataSource={data?.pozadavky || []}
                                pagination={{
                                    total: data?.count,
                                    onChange: page =>
                                        setFilterValues(pv => {
                                            return {
                                                ...pv,
                                                offset: (page - 1) * 10
                                            };
                                        }),
                                    pageSize: 10,
                                    current: filterValues?.offset ? filterValues.offset / 10 + 1 : 1,
                                    showSizeChanger: false
                                }}
                                rowClassName={(_a, i) => {
                                    const classes = [];
                                    if (i % 2) classes.push('tableRowGray');
                                    return classes.join(' ');
                                }}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </LoadingWrapper>
    );
};

export default OmezenaProveditelnost;
